import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, Download, Eye, FileText, File, Image, Paperclip, AlertCircle, CheckCircle } from 'lucide-react';
import cloudinaryService, { UploadedFileInfo, UploadProgress } from '../../services/cloudinaryService';

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  type: string;
  url?: string;
  publicId?: string; // ID Cloudinary pour gestion ultérieure
  uploadedAt: Date | string; // Peut être une Date ou une string ISO
}

interface FileUploadProps {
  label: string;
  description?: string;
  value: UploadedFile[];
  onSave: (files: UploadedFile[]) => void | Promise<void>;
  disabled?: boolean;
  multiple?: boolean;
  acceptedTypes?: string[];
  maxSize?: number; // en MB
  required?: boolean;
  entityType?: 'company' | 'institution' | 'user'; // Pour catégoriser les uploads
  documentCategory?: string; // Catégorie de document
}

// ✨ Track files avec un ref pour éviter les problèmes de sync
const FileUpload: React.FC<FileUploadProps> = ({
  label,
  description,
  value = [],
  onSave,
  disabled = false,
  multiple = true,
  acceptedTypes = ['.pdf', '.doc', '.docx', '.jpg', '.png'],
  maxSize = 5,
  required = false,
  entityType = 'company',
  documentCategory
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [files, setFiles] = useState<UploadedFile[]>(value);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // ✨ SYNC: Synchroniser files quand value change depuis le parent
  // IMPORTANT: Copie profonde pour éviter les mutations croisées entre instances
  useEffect(() => {
    if (!isUploading) {
      const copiedFiles = (value || []).map(f => ({ ...f }));
      setFiles(copiedFiles);
    }
  }, [value, isUploading]);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const validateFile = (file: File): string | null => {
    // Vérifier la taille
    if (file.size > maxSize * 1024 * 1024) {
      return `Le fichier "${file.name}" dépasse la taille maximale de ${maxSize}MB`;
    }

    // Vérifier le type
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!acceptedTypes.includes(fileExtension)) {
      return `Le type de fichier "${fileExtension}" n'est pas accepté`;
    }

    return null;
  };

  const handleFiles = async (files: FileList) => {
    if (disabled) return;

    setIsUploading(true);
    setUploadErrors([]);
    const newFiles: UploadedFile[] = [];
    const errors: string[] = [];
    const progressMap: Record<string, number> = {};

    // Validation préliminaire
    const filesToUpload: File[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const error = validateFile(file);
      
      if (error) {
        errors.push(error);
        continue;
      }
      filesToUpload.push(file);
      progressMap[file.name] = 0;
    }

    setUploadProgress(progressMap);

    // Upload vers Cloudinary
    for (const file of filesToUpload) {
      try {
        const folder = `${entityType}/documents` as any;
        
        const result = await cloudinaryService.uploadFile(
          file,
          folder,
          (progress: UploadProgress) => {
            setUploadProgress(prev => ({
              ...prev,
              [file.name]: progress.percentage
            }));
          }
        );

        // Convertir le résultat Cloudinary en notre format UploadedFile
        const uploadedFile: UploadedFile = {
          id: result.id,
          name: result.name,
          size: result.size,
          type: result.type,
          url: result.url, // URL Cloudinary sécurisée
          publicId: result.publicId,
          uploadedAt: result.uploadedAt
        };

        newFiles.push(uploadedFile);
        console.log(`✅ Fichier "${file.name}" uploadé sur Cloudinary:`, result.url);
      } catch (uploadError) {
        const errorMessage = uploadError instanceof Error 
          ? uploadError.message 
          : `Erreur lors de l'upload de "${file.name}"`;
        errors.push(errorMessage);
        console.error(`❌ Échec upload "${file.name}":`, uploadError);
      }
    }

    if (errors.length > 0) {
      setUploadErrors(errors);
    }

    if (newFiles.length > 0) {
      const updatedFiles = multiple ? [...value, ...newFiles] : newFiles;
      // Sauvegarder les URLs (pas les fichiers!) dans le formulaire
      await onSave(updatedFiles);
      console.log(`📄 ${newFiles.length} fichier(s) prêt(s) - URLs Cloudinary stockées pour envoi au backend`);
    }

    setIsUploading(false);
    setUploadProgress({});
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (disabled) return;
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFiles(files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled && !isDragging) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    // Vérifier si on quitte vraiment la zone de drop
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const handleRemoveFile = async (fileId: string) => {
    if (disabled) return;
    const updatedFiles = value.filter(f => f.id !== fileId);
    await onSave(updatedFiles);
  };

  const handleBrowseClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf': return <File className="h-4 w-4 text-red-500" />;
      case 'doc':
      case 'docx': return <FileText className="h-4 w-4 text-blue-500" />;
      case 'jpg':
      case 'jpeg':
      case 'png': return <Image className="h-4 w-4 text-green-500" />;
      default: return <Paperclip className="h-4 w-4 text-gray-500" />;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      </div>
      
      {description && (
        <p className="text-sm text-gray-500">{description}</p>
      )}

      {/* Zone de drop */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`
          relative border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200
          ${isDragging 
            ? 'border-[#197ca8] bg-blue-50' 
            : 'border-gray-300 hover:border-gray-400'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
        onClick={handleBrowseClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple={multiple}
          accept={acceptedTypes.join(',')}
          onChange={handleFileInput}
          className="hidden"
          disabled={disabled}
        />

        <div className="flex flex-col items-center space-y-2">
          <Upload className={`w-8 h-8 ${isDragging ? 'text-[#197ca8]' : 'text-gray-400'}`} />
          <div className="text-sm">
            <span className="font-medium text-[#197ca8]">
              Cliquez pour parcourir
            </span>
            <span className="text-gray-500"> ou glissez-déposez vos fichiers ici</span>
          </div>
          <div className="text-xs text-gray-500">
            Formats acceptés: {acceptedTypes.join(', ')} • Taille max: {maxSize}MB
            {multiple && ' • Plusieurs fichiers autorisés'}
          </div>
        </div>

        {isUploading && (
          <div className="absolute inset-0 bg-white bg-opacity-90 flex flex-col items-center justify-center rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#197ca8]"></div>
              <span className="text-sm font-medium text-gray-700">Upload vers Cloudinary...</span>
            </div>
            {/* Barres de progression individuelles */}
            <div className="w-full max-w-xs space-y-1">
              {Object.entries(uploadProgress).map(([fileName, progress]) => (
                <div key={fileName} className="text-xs">
                  <div className="flex justify-between text-gray-600 mb-0.5">
                    <span className="truncate max-w-[150px]">{fileName}</span>
                    <span>{progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div 
                      className="bg-[#197ca8] h-1.5 rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Erreurs d'upload */}
      {uploadErrors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex items-start space-x-2">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">Erreurs lors de l'upload :</p>
              <ul className="mt-1 text-sm text-red-700 list-disc list-inside">
                {uploadErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
            <button 
              onClick={() => setUploadErrors([])}
              className="text-red-400 hover:text-red-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Liste des fichiers uploadés */}
      {value.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700">
            Documents joints ({value.length})
          </h4>
          <div className="space-y-2">
            {value.map((file) => (
              <div
                key={file.id}
                className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg"
              >
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  {getFileIcon(file.name)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(file.size)} • {new Date(file.uploadedAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  {file.url && (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(file.url, '_blank');
                        }}
                        className="p-1 text-gray-400 hover:text-[#197ca8] transition-colors"
                        title="Voir le fichier"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const link = document.createElement('a');
                          link.href = file.url!;
                          link.download = file.name;
                          link.click();
                        }}
                        className="p-1 text-gray-400 hover:text-[#197ca8] transition-colors"
                        title="Télécharger"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  {!disabled && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFile(file.id);
                      }}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                      title="Supprimer"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;